import * as Yup from 'yup'
import { emptySpaceCharsAtStartAndEndRegex } from './constants'
import { listingTextRestrictionMessage } from './messagesConstants'

export type ListingTextKind = 'plain' | 'longDescription'

export type ListingTextHighlight = {
  start: number
  end: number
}

const BULLET_PATTERN = /[\u2022\u2023\u2043\u2219\u25E6\u25CF\u25CB]/
const LINE_BREAK_PATTERN = /[\r\n]/
const BOLD_PATTERN = /\*\*/
const PLAIN_TEXT_PATTERN = /^[\x20-\x7E]*$/
const LONG_DESCRIPTION_PATTERN = /^[\n\r\x20-\x7E]*$/
const CONSECUTIVE_SPACES_PATTERN = / {2,}/
const LONG_DESCRIPTION_CONSECUTIVE_SPACES_PATTERN = / {3,}| {2}(?![\r\n])/

const isBlank = (value: unknown): value is '' | null | undefined =>
  value == null || value === ''

const passesPattern = (value: unknown, pattern: RegExp) =>
  isBlank(value) || (typeof value === 'string' && !pattern.test(value))

export type ListingValidationFlag = {
  value?: boolean
}

/**
 * Restrict listing text without an allow-list.
 * ASCII punctuation stays allowed, including semicolons and commas. Non-ASCII symbols, curly quotes,
 * long dashes, bullets, and consecutive spaces are rejected.
 * Long descriptions may include line breaks, **bold**, and a markdown hard break
 * (exactly two spaces before a newline).
 * Restrictions apply only when the listingValidation flag is enabled.
 */
export const applyListingTextRestrictions = (
  schema: Yup.StringSchema,
  kind: ListingTextKind,
  listingValidationFlag?: ListingValidationFlag
): Yup.StringSchema => {
  if (!listingValidationFlag?.value) return schema

  let next = schema.matches(
    emptySpaceCharsAtStartAndEndRegex,
    listingTextRestrictionMessage
  )

  next = next.test(
    'listing-consecutive-spaces',
    listingTextRestrictionMessage,
    value =>
      kind === 'longDescription'
        ? passesPattern(value, LONG_DESCRIPTION_CONSECUTIVE_SPACES_PATTERN)
        : passesPattern(value, CONSECUTIVE_SPACES_PATTERN)
  )

  if (kind === 'plain') {
    next = next.test(
      'listing-line-breaks',
      listingTextRestrictionMessage,
      value => passesPattern(value, LINE_BREAK_PATTERN)
    )
    next = next.test('listing-bold', listingTextRestrictionMessage, value =>
      passesPattern(value, BOLD_PATTERN)
    )
  }

  next = next.test('listing-bullet', listingTextRestrictionMessage, value =>
    passesPattern(value, BULLET_PATTERN)
  )

  next = next.test(
    'listing-restricted-characters',
    listingTextRestrictionMessage,
    value => {
      if (isBlank(value)) return true
      if (typeof value !== 'string') return false
      const pattern =
        kind === 'longDescription'
          ? LONG_DESCRIPTION_PATTERN
          : PLAIN_TEXT_PATTERN
      return pattern.test(value)
    }
  )

  return next
}

const PLAIN_RESTRICTED_CHARACTER_PATTERN = /[^\x20-\x7E]/g
const LONG_DESCRIPTION_RESTRICTED_CHARACTER_PATTERN = /[^\n\r\x20-\x7E]/g
const LEADING_WHITESPACE_PATTERN = /^\s+/
const TRAILING_WHITESPACE_PATTERN = /\s+$/

const collectMatches = (
  value: string,
  pattern: RegExp,
  ranges: ListingTextHighlight[]
) => {
  const flags = pattern.flags.includes('g')
    ? pattern.flags
    : `${pattern.flags}g`
  const matcher = new RegExp(pattern.source, flags)
  let match = matcher.exec(value)
  while (match) {
    if (match[0].length === 0) {
      matcher.lastIndex += 1
    } else {
      ranges.push({
        start: match.index,
        end: match.index + match[0].length,
      })
    }
    match = matcher.exec(value)
  }
}

const mergeHighlights = (ranges: ListingTextHighlight[]) => {
  const sorted = ranges
    .filter(range => range.end > range.start)
    .sort((left, right) => left.start - right.start || left.end - right.end)
  return sorted.reduce<ListingTextHighlight[]>((merged, range) => {
    const last = merged[merged.length - 1]
    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end)
      return merged
    }
    merged.push({ ...range })
    return merged
  }, [])
}

export const getListingTextHighlights = (
  value: string | null | undefined,
  kind: ListingTextKind
): ListingTextHighlight[] => {
  if (isBlank(value) || typeof value !== 'string') return []

  const ranges: ListingTextHighlight[] = []
  collectMatches(value, LEADING_WHITESPACE_PATTERN, ranges)
  collectMatches(value, TRAILING_WHITESPACE_PATTERN, ranges)
  collectMatches(
    value,
    kind === 'longDescription'
      ? LONG_DESCRIPTION_CONSECUTIVE_SPACES_PATTERN
      : CONSECUTIVE_SPACES_PATTERN,
    ranges
  )

  if (kind === 'plain') {
    collectMatches(value, LINE_BREAK_PATTERN, ranges)
    collectMatches(value, BOLD_PATTERN, ranges)
  }

  collectMatches(
    value,
    kind === 'longDescription'
      ? LONG_DESCRIPTION_RESTRICTED_CHARACTER_PATTERN
      : PLAIN_RESTRICTED_CHARACTER_PATTERN,
    ranges
  )

  return mergeHighlights(ranges)
}
